import { Dialog as RadixDialog } from "radix-ui";
import { type ComponentPropsWithRef, type ReactNode, useEffect, useId, useState } from "react";
import { cx } from "../../lib/cx";
import styles from "./AppShell.module.css";

export interface AppShellNavItem {
  label: string;
  href: string;
  /** Decorative icon (hidden from assistive tech). */
  icon?: ReactNode;
  active?: boolean;
  /** Small trailing marker, e.g. a count or `<Badge>`. */
  badge?: ReactNode;
}

export interface AppShellNavGroup {
  /** Section label; omit for the first, unlabeled group. */
  label?: string;
  items: AppShellNavItem[];
}

export interface AppShellLinkProps {
  href: string;
  className: string;
  "aria-current"?: "page";
  /** Closes the mobile drawer; call it when the link is activated. */
  onClick?: () => void;
  children: ReactNode;
}

export interface AppShellProps extends ComponentPropsWithRef<"div"> {
  /** Logo / product name at the top of the sidebar (and the mobile top bar). */
  brand: ReactNode;
  navigation: AppShellNavGroup[];
  /** Accessible name of the sidebar navigation. Default "Main". */
  navLabel?: string;
  /** Top bar content: search, theme toggle, user menu. */
  topBar?: ReactNode;
  /** Pinned to the bottom of the sidebar (plan, help link, …). */
  sidebarFooter?: ReactNode;
  /** Renders nav links (pass the router's Link). Default: `<a>`. */
  renderLink?: (props: AppShellLinkProps) => ReactNode;
  /** Id of `<main>`, the skip link target. Default "main-content". */
  mainId?: string;
  children: ReactNode;
}

/** Below this width the sidebar becomes a drawer; mirrors the CSS breakpoint. */
const NARROW_QUERY = "(max-width: 63.999rem)";

const defaultLink = ({ children, ...props }: AppShellLinkProps) => <a {...props}>{children}</a>;

function MenuIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 5.5h14M3 10h14M3 14.5h14" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M5 5l10 10M15 5L5 15" />
    </svg>
  );
}

interface NavListProps {
  navigation: AppShellNavGroup[];
  label: string;
  renderLink: (props: AppShellLinkProps) => ReactNode;
  onNavigate?: () => void;
}

function NavList({ navigation, label, renderLink, onNavigate }: NavListProps) {
  const baseId = useId();
  return (
    <nav aria-label={label} className={styles.nav}>
      {navigation.map((group, g) => {
        const headingId = group.label ? `${baseId}-group-${g}` : undefined;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: groups are static configuration
          <div key={g} className={styles.group}>
            {group.label && (
              // Not a heading: section labels would clutter every page's outline.
              <div id={headingId} className={styles.groupLabel}>
                {group.label}
              </div>
            )}
            <ul className={styles.list} aria-labelledby={headingId}>
              {group.items.map((item) => (
                <li key={item.href}>
                  {renderLink({
                    href: item.href,
                    className: styles.item ?? "",
                    "aria-current": item.active ? "page" : undefined,
                    onClick: onNavigate,
                    children: (
                      <>
                        {item.icon && (
                          <span className={styles.itemIcon} aria-hidden="true">
                            {item.icon}
                          </span>
                        )}
                        <span className={styles.itemLabel}>{item.label}</span>
                        {item.badge !== undefined && item.badge !== null && (
                          <span className={styles.itemBadge}>{item.badge}</span>
                        )}
                      </>
                    ),
                  })}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

/**
 * Product layout: skip link, sidebar navigation, top bar and main content.
 * Under 64rem the sidebar moves into a modal drawer opened from the top bar.
 */
export function AppShell({
  brand,
  navigation,
  navLabel = "Main",
  topBar,
  sidebarFooter,
  renderLink = defaultLink,
  mainId = "main-content",
  className,
  children,
  ...rest
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer if the viewport grows past the breakpoint while it's open.
  useEffect(() => {
    const media = window.matchMedia?.(NARROW_QUERY);
    if (!media) return;
    const onChange = (event: MediaQueryListEvent) => {
      if (!event.matches) setDrawerOpen(false);
    };
    media.addEventListener?.("change", onChange);
    return () => media.removeEventListener?.("change", onChange);
  }, []);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div {...rest} className={cx(styles.shell, className)}>
      <a href={`#${mainId}`} className={styles.skipLink}>
        Skip to main content
      </a>

      <aside className={styles.sidebar}>
        <div className={styles.brand}>{brand}</div>
        <NavList navigation={navigation} label={navLabel} renderLink={renderLink} />
        {sidebarFooter && <div className={styles.sidebarFooter}>{sidebarFooter}</div>}
      </aside>

      <div className={styles.body}>
        <header className={styles.topBar}>
          <RadixDialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <RadixDialog.Trigger asChild>
              <button type="button" className={styles.menuButton} aria-label="Open navigation">
                <MenuIcon />
              </button>
            </RadixDialog.Trigger>
            <RadixDialog.Portal>
              <RadixDialog.Overlay className={styles.overlay} />
              <RadixDialog.Content className={styles.drawer} aria-describedby={undefined}>
                <div className={styles.drawerHeader}>
                  <RadixDialog.Title className="wv-visually-hidden">Navigation</RadixDialog.Title>
                  <div className={styles.brand}>{brand}</div>
                  <RadixDialog.Close asChild>
                    <button
                      type="button"
                      className={styles.menuButton}
                      aria-label="Close navigation"
                    >
                      <CloseIcon />
                    </button>
                  </RadixDialog.Close>
                </div>
                <NavList
                  navigation={navigation}
                  label={navLabel}
                  renderLink={renderLink}
                  onNavigate={closeDrawer}
                />
                {sidebarFooter && <div className={styles.sidebarFooter}>{sidebarFooter}</div>}
              </RadixDialog.Content>
            </RadixDialog.Portal>
          </RadixDialog.Root>
          <div className={styles.topBarBrand}>{brand}</div>
          <div className={styles.topBarContent}>{topBar}</div>
        </header>

        <main id={mainId} tabIndex={-1} className={styles.main}>
          <div className={styles.content}>{children}</div>
        </main>
      </div>
    </div>
  );
}
